export type BrandLevel = 'none' | 'colour' | 'full'

export interface Chapter {
  snak: 1 | 2 | 3
  label: string
  archetype: string
  verb: string
  differentiation: string
  audience: string
  goal: string
  brand: BrandLevel
  brandNote: string
}

export const chapters: Chapter[] = [
  {
    snak: 1,
    label: 'Snak 1',
    archetype: 'Foundational',
    verb: 'Spark precision',
    differentiation: 'Preserve',
    audience: 'Treats DME by fluid volume. Has not yet questioned the one-size-fits-all approach.',
    goal: 'Establish that DME is not one morphological pattern.',
    brand: 'none',
    brandNote: 'Completely unbranded',
  },
  {
    snak: 2,
    label: 'Snak 2',
    archetype: 'Developing',
    verb: 'Embed precision',
    differentiation: 'Prevent',
    audience: 'Accepts that phenotypes exist. Has not yet made them part of the routine.',
    goal: 'Make phenotyping operational — and introduce the window of opportunity.',
    brand: 'colour',
    brandNote: 'Brand colours, no logo',
  },
  {
    snak: 3,
    label: 'Snak 3',
    archetype: 'Advanced',
    verb: 'Ignite precision',
    differentiation: 'Predict',
    audience: 'Already practises precision medicine. Wants infrastructure, not persuasion.',
    goal: 'Move from characterising to piloting and predicting.',
    brand: 'full',
    brandNote: 'Fully branded',
  },
]

export function chapterFor(snak: number): Chapter {
  return chapters.find((c) => c.snak === snak) ?? chapters[0]
}

export const brandLogo = {
  src: 'https://cdn.magicpatterns.com/uploads/2oBPzTf8yTj7VjZGNcPz54/Screenshot_2026-08-19_at_14.28.28.png',
  alt: 'Ozurdex (dexamethasone intravitreal implant) 0.7 mg',
}

export const entry = {
  eyebrow: 'Precision medicine adoption · Denmark',
  headline: ['Three instalments.', 'One argument.'],
  lead: 'From revealing the phenotype, through sharper diagnosis, to precision treatment — each instalment matched to where the specialist already sits.',
  openLabel: 'Choose an instalment',
  openDetail: 'Three campaigns, or one presentation',
  journeyLabel: 'See the journey',
  menuTitle: 'Where does this specialist sit?',
  backLabel: 'Back',
  campaignLabel: 'Run one instalment',
  presentationLabel: 'Run all three',
  presentationDetail: '15 screens · face-to-face presentation mode',
  footnote:
    'HCP archetypes after the AbbVie precision-medicine adoption model: Foundational, Developing, Advanced.',
}

export interface JourneyStage {
  snak: 1 | 2 | 3
  arc: string
  title: string
  subtitle: string
  /** Weight of pure scientific content, 0–100. */
  science: number
  /** Brand presence, 0–100. */
  brandWeight: number
  trust: string
  register: string
  visual: string
  ask: string
  palette: string[]
}

export const journeyStages: JourneyStage[] = [
  {
    snak: 1,
    arc: 'Reveal',
    title: 'Reveal the phenotype',
    subtitle: 'DME is not one morphological pattern',
    science: 100,
    brandWeight: 0,
    trust: 'Trust has to be earned before anything at all is claimed.',
    register: 'Pure science. Published data, no product, no voice of a company.',
    visual: 'Unbranded. Near-monochrome, clinical OCT imagery, black on white.',
    ask: 'Book a phenotyping walkthrough',
    palette: ['#0B2A4A', '#1E76CF', '#F2F8F9'],
  },
  {
    snak: 2,
    arc: 'Enhance',
    title: 'Enhance the diagnosis',
    subtitle: 'The window closes quietly — make phenotyping routine',
    science: 65,
    brandWeight: 45,
    trust: 'Credibility is established; it now has to become useful in clinic.',
    register: 'Applied science. Timing, review rhythms, documentation.',
    visual: 'Brand colours, no logo. Warmer surface, colour used to structure.',
    ask: 'Book a proactive-management case review',
    palette: ['#203864', '#4C7C9B', '#EEF3F6'],
  },
  {
    snak: 3,
    arc: 'Prescribe',
    title: 'Precision treatment',
    subtitle: 'Pilot it, then predict it',
    science: 40,
    brandWeight: 100,
    trust: 'Trust converts into a treatment conversation and a shared project.',
    register: 'Product and partnership. Named therapy, openly aspirational science.',
    visual: 'Fully branded. Dark navy canvas, full-bleed imagery, green accents, logo present.',
    ask: 'Join the pilot · prescribe-level discussion',
    palette: ['#203864', '#A6BE39', '#4C7C9B'],
  },
]

export const journeyMeta = {
  title: 'The journey',
  subtitle: 'From revealing the phenotype to precision treatment',
  axisLabel: 'Precision medicine adoption',
  axisStart: 'Low',
  axisEnd: 'High',
  scienceLabel: 'Scientific weight',
  brandLabel: 'Brand presence',
  closing:
    'As evidence accumulates the trust requirement falls and brand presence rises. The design follows the same curve: monochrome and clinical at the start, colour-led and branded at the end.',
}
